import {
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import { IntlTelInputNumberFormat, IntlTelInputValidationError } from './intl-tel-input.constants';

// Declare window interface for intlTelInputUtils
declare global {
  interface Window {
    intlTelInputUtils: any;
  }
}

// Export the number format enum for use in components
export { IntlTelInputNumberFormat, IntlTelInputValidationError, COUNTRY_CODES } from './intl-tel-input.constants';

@Directive({
  selector: '[appIntlTelInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IntlTelInputDirective),
      multi: true
    }
  ]
})
export class IntlTelInputDirective implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() initialCountry: string = 'ca'; // Default to Canada
  @Input() preferredCountries: string[] = ['ca', 'us'];
  @Input() onlyCountries: string[] = [];
  @Input() separateDialCode: boolean = true; // Show country dial code
  @Input() nationalMode: boolean = true;
  @Input() formatOnDisplay: boolean = false; // Don't format on display
  @Input() allowDropdown: boolean = true; // Allow country dropdown
  @Input() autoInsertDialCode: boolean = false; // Don't auto-insert dial code

  @Input() showErrorText: boolean = false; // Show error message below input

  @Output() onComplete = new EventEmitter<string>();
  @Output() onBlur = new EventEmitter<string>();
  @Output() countryChange = new EventEmitter<any>();
  @Output() validationChange = new EventEmitter<boolean>();
  @Output() errorMessageChange = new EventEmitter<string>();
  @Output() countryCodeChange = new EventEmitter<string>(); // Emits country code only (e.g., "+1")
  @Output() telephoneNumberChange = new EventEmitter<string>(); // Emits national number only (e.g., "4165551234")
  @Output() fullTelephoneNumberChange = new EventEmitter<string>(); // Emits full international number (e.g., "+14165551234")

  private iti: any;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private isManualCountryChange: boolean = false;
  private currentCountry: string = '';
  private errorElement: HTMLElement | null = null;

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    this.initializeIntlTelInput();
  }

  ngOnDestroy(): void {
    if (this.iti) {
      this.iti.destroy();
    }
    // Remove error element if it exists
    if (this.errorElement && this.errorElement.parentNode) {
      this.errorElement.parentNode.removeChild(this.errorElement);
    }
  }

  private initializeIntlTelInput(): void {
    const input = this.el.nativeElement;

    const options: any = {
      initialCountry: this.initialCountry,
      preferredCountries: this.preferredCountries,
      separateDialCode: this.separateDialCode,
      nationalMode: this.nationalMode,
      autoPlaceholder: 'off', // Disable auto placeholder
      formatOnDisplay: this.formatOnDisplay,
      allowDropdown: this.allowDropdown,
      autoInsertDialCode: this.autoInsertDialCode,
      geoIpLookup: null, // Disable geo IP lookup
      utilsScript: 'assets/js/intl-tel-input-utils.js'
    };

    if (this.onlyCountries && this.onlyCountries.length > 0) {
      options.onlyCountries = this.onlyCountries;
    }

    this.iti = intlTelInput(input, options);

    // Store the initial country
    this.currentCountry = this.initialCountry;

    // Emit the initial country code so consumers have it from the start
    setTimeout(() => {
      this.emitPhoneNumberParts();
    }, 0);

    // Add w-100 class to the wrapper div created by intl-tel-input
    const itiContainer = input.parentElement;
    if (itiContainer && itiContainer.classList.contains('iti')) {
      itiContainer.classList.add('w-100');

      // Set dropdown width to match container width
      setTimeout(() => {
        const dropdown = document.querySelector('.iti__country-list') as HTMLElement;
        if (dropdown && itiContainer) {
          dropdown.style.width = itiContainer.offsetWidth + 'px';
        }
      }, 0);
    }

    // Listen to input events
    input.addEventListener('input', () => {
      this.sanitizeInput();
      this.handleInput();
      // Prevent automatic country change when typing
      this.preventAutoCountryChange();
    });

    // Prevent non-numeric input on keypress
    input.addEventListener('keypress', (e: KeyboardEvent) => {
      this.handleKeyPress(e);
    });

    // Handle paste events to sanitize pasted content
    input.addEventListener('paste', (e: ClipboardEvent) => {
      this.handlePaste(e);
    });

    input.addEventListener('blur', () => {
      this.handleBlur();
    });

    input.addEventListener('countrychange', () => {
      this.handleCountryChange();
    });

    // Listen for dropdown open event to set width
    input.addEventListener('open:countrydropdown', () => {
      this.setDropdownWidth();
      this.attachDropdownClickListener();
    });

    // Also set on focus (when dropdown might open)
    input.addEventListener('focus', () => {
      setTimeout(() => this.setDropdownWidth(), 50);
    });

    // Create error element if showErrorText is enabled
    if (this.showErrorText) {
      this.createErrorElement();
    }
  }

  private sanitizeInput(): void {
    const input = this.el.nativeElement;
    const cursorPosition = input.selectionStart;
    const originalValue = input.value;

    // Remove all non-numeric characters except spaces, hyphens, parentheses, and plus
    // These are allowed for phone number formatting
    const sanitized = originalValue.replace(/[^\d\s\-\(\)\+]/g, '');

    if (sanitized !== originalValue) {
      input.value = sanitized;
      // Restore cursor position (adjusted for removed characters)
      const removedChars = originalValue.length - sanitized.length;
      input.setSelectionRange(cursorPosition - removedChars, cursorPosition - removedChars);
    }
  }

  private handleKeyPress(e: KeyboardEvent): void {
    const char = e.key;

    // Allow: backspace, delete, tab, escape, enter
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(char)) {
      return;
    }

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    // Allow: numbers (0-9), space, hyphen, parentheses, plus
    const allowedChars = /[\d\s\-\(\)\+]/;
    if (!allowedChars.test(char)) {
      e.preventDefault();
    }
  }

  private handlePaste(e: ClipboardEvent): void {
    e.preventDefault();

    const pastedText = e.clipboardData?.getData('text') || '';
    // Remove all non-numeric characters except spaces, hyphens, parentheses, and plus
    const sanitized = pastedText.replace(/[^\d\s\-\(\)\+]/g, '');

    // Insert sanitized text at cursor position
    const input = this.el.nativeElement;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const currentValue = input.value;

    input.value = currentValue.substring(0, start) + sanitized + currentValue.substring(end);

    // Set cursor position after pasted text
    const newPosition = start + sanitized.length;
    input.setSelectionRange(newPosition, newPosition);

    // Trigger input event to update the model
    input.dispatchEvent(new Event('input'));
  }

  private handleInput(): void {
    // Get the full number including country code if separateDialCode is true
    const value = this.separateDialCode && this.iti ? this.iti.getNumber() : this.el.nativeElement.value;
    this.onChange(value);

    // Emit validation state
    const isValid = this.iti ? this.iti.isValidNumber() : false;
    this.validationChange.emit(isValid);

    // Get and emit error message
    const errorMessage = this.getErrorMessage();
    this.errorMessageChange.emit(errorMessage);

    // Update error element display
    this.updateErrorDisplay(errorMessage);

    // Emit separate phone number parts
    this.emitPhoneNumberParts();

    // Check if number is complete (valid)
    if (isValid) {
      this.onComplete.emit(this.getFormattedNumber());
    }
  }

  private handleBlur(): void {
    this.onTouched();
    const value = this.getFormattedNumber();
    this.onBlur.emit(value);
  }

  private handleCountryChange(): void {
    const countryData = this.iti.getSelectedCountryData();

    // Only allow manual country changes (from dropdown)
    if (this.isManualCountryChange) {
      this.currentCountry = countryData.iso2;
      this.countryChange.emit(countryData);
      this.isManualCountryChange = false;
      // Emit updated phone number parts so consumers receive the new country code
      this.emitPhoneNumberParts();
    } else {
      // Revert to the current country if it was an automatic change
      if (this.iti && countryData.iso2 !== this.currentCountry) {
        setTimeout(() => {
          this.iti.setCountry(this.currentCountry);
        }, 0);
      }
    }
  }

  private preventAutoCountryChange(): void {
    // Ensure the country stays as the current selected country
    if (this.iti) {
      const selectedCountry = this.iti.getSelectedCountryData();
      if (selectedCountry.iso2 !== this.currentCountry) {
        this.iti.setCountry(this.currentCountry);
      }
    }
  }

  private setDropdownWidth(): void {
    const itiContainer = this.el.nativeElement.parentElement;
    const dropdown = document.querySelector('.iti__country-list') as HTMLElement;

    if (dropdown && itiContainer) {
      const containerWidth = itiContainer.offsetWidth - 12;
      dropdown.style.width = containerWidth + 'px';
      dropdown.style.minWidth = containerWidth + 'px';
      dropdown.style.maxWidth = containerWidth + 'px';
    }
  }

  private attachDropdownClickListener(): void {
    // Mark that the next country change is manual (from dropdown)
    const dropdown = document.querySelector('.iti__country-list') as HTMLElement;
    if (dropdown) {
      const countries = dropdown.querySelectorAll('.iti__country');
      countries.forEach(country => {
        country.addEventListener('click', () => {
          this.isManualCountryChange = true;
        }, { once: true });
      });
    }
  }

  private getFormattedNumber(): string {
    if (!this.iti) {
      return this.el.nativeElement.value;
    }

    // If separateDialCode is true, return full international number
    // Otherwise return national format
    if (window.intlTelInputUtils) {
      if (this.separateDialCode) {
        return this.iti.getNumber(IntlTelInputNumberFormat.E164);
      } else {
        return this.iti.getNumber(IntlTelInputNumberFormat.NATIONAL);
      }
    }
    return this.iti.getNumber();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value !== undefined && value !== null && value !== '') {
      if (this.iti) {
        // If separateDialCode is true, we need to extract only the national number
        if (this.separateDialCode) {
          // Set the number to detect country (this may format it)
          this.iti.setNumber(value);

          setTimeout(() => {
            const countryData = this.iti.getSelectedCountryData();
            const dialCode = countryData.dialCode;

            // Remove all non-digits from the value
            let digitsOnly = value.replace(/\D/g, '');

            // Remove the country code from the beginning if present
            if (dialCode && digitsOnly.startsWith(dialCode)) {
              digitsOnly = digitsOnly.substring(dialCode.length);
            }

            // Set only the national number (without country code) in the input
            this.el.nativeElement.value = digitsOnly;
          }, 0);
        } else {
          // If not using separate dial code, just set the raw value
          this.el.nativeElement.value = value;
        }
      } else {
        // If iti not initialized yet, just set the value
        this.el.nativeElement.value = value;
      }
    } else if (value === '' || value === null) {
      // Clear the input
      this.el.nativeElement.value = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  // Public methods for validation
  public isValidNumber(): boolean {
    return this.iti ? this.iti.isValidNumber() : false;
  }

  public getValidationError(): number | null {
    return this.iti ? this.iti.getValidationError() : null;
  }

  public getNumber(format?: number): string {
    if (!this.iti) {
      return this.el.nativeElement.value;
    }
    if (format !== undefined && window.intlTelInputUtils) {
      return this.iti.getNumber(format);
    }
    return this.iti.getNumber();
  }

  public setCountry(countryCode: string): void {
    if (this.iti) {
      this.isManualCountryChange = true;
      this.currentCountry = countryCode;
      this.iti.setCountry(countryCode);
    }
  }

  // Emit separate phone number parts
  private emitPhoneNumberParts(): void {
    if (!this.iti) {
      return;
    }

    const countryData = this.iti.getSelectedCountryData();
    const countryCode = countryData.dialCode ? `+${countryData.dialCode}` : '';

    // Always emit the country code — it is available without utils
    this.countryCodeChange.emit(countryCode);

    // Get the full international number and national number (requires utils)
    const fullNumber = this.iti.getNumber(IntlTelInputNumberFormat.E164) || '';
    let nationalNumber = this.el.nativeElement.value.replace(/\D/g, '');

    if (window.intlTelInputUtils && fullNumber && countryCode) {
      const countryCodeDigits = countryCode.replace(/\D/g, '');
      if (fullNumber.startsWith('+' + countryCodeDigits)) {
        nationalNumber = fullNumber.substring(countryCodeDigits.length + 1);
      }
    }

    this.telephoneNumberChange.emit(nationalNumber);
    this.fullTelephoneNumberChange.emit(fullNumber);
  }

  // Get country code only (e.g., "+1")
  public getCountryCode(): string {
    if (!this.iti) {
      return '';
    }
    const countryData = this.iti.getSelectedCountryData();
    return countryData.dialCode ? `+${countryData.dialCode}` : '';
  }

  // Get telephone number only (without country code)
  public getTelephoneNumberOnly(): string {
    if (!this.iti) {
      return '';
    }

    const fullNumber = this.iti.getNumber(IntlTelInputNumberFormat.E164) || '';
    const countryCode = this.getCountryCode();

    if (fullNumber && countryCode) {
      const countryCodeDigits = countryCode.replace(/\D/g, '');
      if (fullNumber.startsWith('+' + countryCodeDigits)) {
        return fullNumber.substring(countryCodeDigits.length + 1);
      }
    }

    // Fallback: return digits from input
    return this.el.nativeElement.value.replace(/\D/g, '');
  }

  // Get full telephone number (with country code in E164 format)
  public getFullTelephoneNumber(): string {
    if (!this.iti) {
      return '';
    }
    return this.iti.getNumber(IntlTelInputNumberFormat.E164) || '';
  }

  // Get human-readable error message
  public getErrorMessage(): string {
    if (!this.iti) {
      return '';
    }

    const inputValue = this.el.nativeElement.value.trim();

    // No error if input is empty
    if (!inputValue) {
      return '';
    }

    // Check if number is valid
    if (this.iti.isValidNumber()) {
      return '';
    }

    // Get validation error code
    const errorCode = this.iti.getValidationError();

    // Map error codes to user-friendly messages
    switch (errorCode) {
      case IntlTelInputValidationError.INVALID_COUNTRY_CODE:
        return 'Invalid country code';
      case IntlTelInputValidationError.TOO_SHORT:
        return 'Phone number is too short';
      case IntlTelInputValidationError.TOO_LONG:
        return 'Phone number is too long';
      case IntlTelInputValidationError.INVALID_LENGTH:
        return 'Phone number has invalid length';
      case IntlTelInputValidationError.IS_POSSIBLE_LOCAL_ONLY:
        return 'Phone number is only valid locally';
      default:
        return 'Invalid phone number';
    }
  }

  // Create error message element
  private createErrorElement(): void {
    const itiContainer = this.el.nativeElement.parentElement;
    if (!itiContainer) {
      return;
    }

    this.errorElement = document.createElement('div');
    this.errorElement.className = 'intl-tel-error-message';
    this.errorElement.style.color = '#dc3545';
    this.errorElement.style.fontSize = '0.875rem';
    this.errorElement.style.marginTop = '0.25rem';
    this.errorElement.style.display = 'none';

    // Insert after the iti container
    if (itiContainer.nextSibling) {
      itiContainer.parentNode?.insertBefore(this.errorElement, itiContainer.nextSibling);
    } else {
      itiContainer.parentNode?.appendChild(this.errorElement);
    }
  }

  // Update error message display
  private updateErrorDisplay(errorMessage: string): void {
    if (!this.showErrorText || !this.errorElement) {
      return;
    }

    if (errorMessage) {
      this.errorElement.textContent = errorMessage;
      this.errorElement.style.display = 'block';
    } else {
      this.errorElement.style.display = 'none';
    }
  }
}
